import React, { useState } from 'react';
import Form, { Field, HelperMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Button from '@atlaskit/button';
import { N90 } from '@atlaskit/theme/colors';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import './custom-search.scss';

interface FormData {
  searchTerm: string;
}

interface CustomSearchProps {
  placeholder?: string;
  onSearch: (formData: FormData) => void;
  IconView?: string;
  isLoading?: boolean;
  helperMessage?: string;
  isClearable?: boolean;
  isRefetchOnClearEnabled?: boolean;
  isSearchableOnChange?: boolean;
  shouldTrimInput?: boolean;
}

export const CustomSearch = ({
  placeholder,
  onSearch,
  isLoading,
  IconView = 'searchIcon',
  helperMessage,
  isClearable = false,
  isRefetchOnClearEnabled = true,
  isSearchableOnChange = false,
  shouldTrimInput = false,
}: CustomSearchProps) => {
  const [fieldValue, setFieldValue] = useState<string>('');
  const [submittedFieldValue, setSubmittedFieldValue] = useState<string>();

  const handleSearch = (formdata: FormData) => {
    if (!isSearchableOnChange) {
      formdata.searchTerm = fieldValue;
    }
    setSubmittedFieldValue(formdata.searchTerm);
    onSearch && onSearch(formdata);
  };

  const handleReset = () => {
    setFieldValue('');
    // cases to handle here
    // if there is some text added and form is not submitted, submittedFieldValue = undefined, fieldValue = "string" => no refetch
    // if search results are based on a fieldValue "string" and then user press reset, submittedFieldValue = "string", fieldValue = "" => refetch
    // if search results are based on a fieldValue "string" and then user types "otherString" and press reset, submittedFieldValue="string", fieldValue="otherString" => reset
    if (isRefetchOnClearEnabled && submittedFieldValue) {
      onSearch && onSearch({ searchTerm: '' });
    }
  };

  return (
    <div className="custom-search-wrapper">
      <Form<FormData>
        onSubmit={(formdata) => {
          if (!isSearchableOnChange) {
            handleSearch(formdata);
          }
        }}
      >
        {({ formProps }) => (
          <form data-testid="custom-search-form" {...formProps}>
            <span className="no-label-field">
              <Field name="searchTerm" defaultValue="">
                {({ fieldProps }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      value={fieldValue}
                      onChange={(e) => {
                        setFieldValue(shouldTrimInput ? e.currentTarget.value.trim() : e.currentTarget.value);
                        if (isSearchableOnChange) {
                          handleSearch({ searchTerm: e.currentTarget.value });
                        }
                      }}
                      isCompact={true}
                      role="search"
                      isDisabled={isLoading}
                      aria-label={placeholder}
                      placeholder={placeholder}
                      testId="custom-search-input"
                      className="custom-search-input"
                      elemBeforeInput={
                        isClearable ? (
                          IconView === 'searchIcon' ? (
                            <div className="search-icon-before" data-testId="search-icon-before">
                              <SearchIcon primaryColor={N90} label="" size="small" />
                            </div>
                          ) : null
                        ) : null
                      }
                      elemAfterInput={
                        isClearable ? (
                          fieldValue.length !== 0 ? (
                            <Button
                              appearance="subtle"
                              className="clear-icon-wrapper"
                              onClick={handleReset}
                              testId="search-clear-icon"
                              iconBefore={<SelectClearIcon primaryColor={N90} label="" size="small" />}
                            ></Button>
                          ) : null
                        ) : IconView === 'searchIcon' ? (
                          <div className="search-icon-wrapper" data-testId="search-icon-after">
                            <SearchIcon label="" size="small" />
                          </div>
                        ) : null
                      }
                    />
                    {helperMessage && <HelperMessage>{helperMessage}</HelperMessage>}
                  </>
                )}
              </Field>
            </span>
            {/* submits the form on enter */}
            <input type="submit" data-testid="submit-search" style={{ display: 'none' }} />
          </form>
        )}
      </Form>
    </div>
  );
};
